import { Component, Inject, OnInit , signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountService } from 'src/app/services/account/account.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { MapService } from 'src/app/services/map/map.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { MapElementType } from 'src/app/models/map-element-type';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { HistoryElement } from '../../../models/history';

/**
 * a set of trips that happened on the same calendar day, shown under one dropdown
 */
export interface HistoryGroup {
  date: Date;
  trips: HistoryElement[];
  opened: boolean;
}
import { ECar } from 'src/app/models/ecar';
import { CommonModule } from '@angular/common';
import { TurnValueIntegerThousandsPipe } from 'src/app/pipes/turn-value-integer-thousands.pipe';
import { UserVehiclesSelectorComponent } from './user-vehicles-selector/user-vehicles-selector.component';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';

/**
   * display connected user previous trips
   */
@Component({
  standalone: true,
  selector: 'app-history-dialog',
  templateUrl: './history-dialog.component.html',
  imports: [CommonModule, TurnValueIntegerThousandsPipe, UserVehiclesSelectorComponent, EvLoaderComponent],
  styleUrls: ['./history-dialog.component.scss']
})

export class HistoryDialogComponent implements OnInit {

  public readonly historyLoader = signal<boolean>(false);
  public readonly historyLoaded = signal<boolean>(false);
  public readonly historyList = signal<HistoryElement[]>([]);
  public readonly historyGroups = signal<HistoryGroup[]>([]);
  public readonly selectedUserVehicle = signal<ECar | null>(null);


  constructor(public dialogRef: MatDialogRef<HistoryDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    public languageService: LanguageService, public accountService: AccountService,
    public settingsService: SettingsService, private stackedModalsService: StackedModalsService,
    private mapService: MapService, private consoleLoggerService: ConsoleLoggerService) { }



  ngOnInit(): void {
  }

  public setSelectedUserVehicle(selectedECar: ECar): void {
    // setTimeout for fixing ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.historyLoader.set(true);
      this.accountService.getHistory(selectedECar.userVehicleID).subscribe((resp) => {
        if (resp) {
          this.consoleLoggerService.log(resp);
          this.historyList.set(resp);
          this.historyGroups.set(this.openFirstGroups(this.buildHistoryGroups(resp)));
          this.historyLoader.set(false);
          this.historyLoaded.set(true);
          this.selectedUserVehicle.set(selectedECar);
        }
      });
    });
  }

  /**
   * groups trips that share the same calendar day into a single dropdown group,
   * preserving the incoming order (list is already sorted by date)
   */
  private buildHistoryGroups(historyList: HistoryElement[]): HistoryGroup[] {
    const groups: HistoryGroup[] = [];
    for (const trip of historyList) {
      const tripDate = new Date(trip.startTime);
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && this.isSameDay(lastGroup.date, tripDate)) {
        lastGroup.trips.push(trip);
      } else {
        groups.push({ date: tripDate, trips: [trip], opened: false });
      }
    }
    return groups;
  }

  /** Opens the most recent day-groups, up to ~4 trips, before the list is handed to the signal. */
  private openFirstGroups(groups: HistoryGroup[]): HistoryGroup[] {
    let openedTrips = 0;
    return groups.map((group) => {
      if (openedTrips >= 4) {
        return group;
      }
      openedTrips += group.trips.length;
      return { ...group, opened: true };
    });
  }

  /** Groups are replaced, not mutated: flipping `opened` in place would notify nothing. */
  public toggleHistoryGroup(index: number): void {
    this.historyGroups.update(groups => groups.map((group, i) => i === index ? { ...group, opened: !group.opened } : group));
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  public openRouteHistoryOnMap(historyElement: HistoryElement): void {
    this.accountService.setHistoryLastSelectedVehicle(this.selectedUserVehicle());
    this.mapService.ObservableShowOnMap.next({ type: MapElementType.RouteHistory, data: historyElement });
    this.dialogRef.close();
  }

  public removeHistoryElement(event, historyItem: HistoryElement): void {
    event.stopPropagation();
    let dialogRef = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.HistoryDialog_Delete_Title,
        message: this.languageService.languageJSON.HistoryDialog_Delete_Desc,
        buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
      },
      panelClass: 'delete-confirmation',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.selectedUserVehicle()) {
        this.consoleLoggerService.log(historyItem.id);
        this.accountService.deleteLogById(this.selectedUserVehicle()!.userVehicleID, historyItem.id).subscribe(() => {
          this.setSelectedUserVehicle(this.selectedUserVehicle()!);
        });
      }
    });
  }

  public removeAllHistoryElement(): void {
    let dialogRef = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.HistoryDialog_DeleteAll_Title,
        message: this.languageService.languageJSON.HistoryDialog_DeleteAll_Desc,
        buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
      },
      panelClass: 'delete-confirmation',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.selectedUserVehicle()) {
        this.accountService.deleteLogByVehicle(this.selectedUserVehicle()!.userVehicleID).subscribe(() => {
          this.setSelectedUserVehicle(this.selectedUserVehicle()!);
        });
      }
    });
  }

  public close(): void {
    this.dialogRef.close();
  }
}
